import { useState } from 'react';
import clsx from 'clsx';
import type {
  HomeLayout,
  Tile,
  TileGroup,
  Widget,
  WidgetKind,
  SidebarItem,
  SidebarSection,
  TileColor,
} from '../../lib/home-schema.js';
import { TILE_COLORS, TILE_COLOR_OPTIONS } from '../../lib/tile-colors.js';
import { LogoUploader } from './LogoUploader.js';

interface EditPanelProps {
  layout: HomeLayout;
  onChange: (next: HomeLayout) => void;
  onClose: () => void;
  onReset: () => void;
}

type Tab = 'groups' | 'sidebar' | 'widgets' | 'brand';

export function EditPanel({ layout, onChange, onClose, onReset }: EditPanelProps) {
  const [tab, setTab] = useState<Tab>('groups');

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-[420px] flex-col border-l border-stone-200 bg-stone-50 shadow-2xl">
      <div className="flex items-center justify-between border-b border-stone-200 bg-white px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-stone-800">Edit this page</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-400">
            All changes save automatically
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded bg-stone-900 px-3 py-1 text-xs text-white hover:bg-stone-700"
          type="button"
        >
          Done
        </button>
      </div>
      <div className="flex gap-1 border-b border-stone-200 bg-white px-3 pt-2">
        {(['groups', 'sidebar', 'widgets', 'brand'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={clsx(
              'rounded-t px-3 py-1.5 text-xs font-medium capitalize',
              tab === t ? 'bg-stone-100 text-stone-900' : 'text-stone-500 hover:text-stone-800',
            )}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'groups' && <GroupsEditor layout={layout} onChange={onChange} />}
        {tab === 'sidebar' && <SidebarEditor layout={layout} onChange={onChange} />}
        {tab === 'widgets' && <WidgetsEditor layout={layout} onChange={onChange} />}
        {tab === 'brand' && <BrandEditor layout={layout} onChange={onChange} />}
      </div>
      <div className="border-t border-stone-200 bg-white px-4 py-3">
        <button
          type="button"
          onClick={() => {
            if (confirm('Reset home base to the SFP defaults? Your customizations will be lost.')) {
              onReset();
            }
          }}
          className="text-xs text-stone-500 hover:text-[#9C4A3A]"
        >
          Reset to SFP defaults
        </button>
      </div>
    </div>
  );
}

/* ----------------------------------- groups ----------------------------------- */

function GroupsEditor({ layout, onChange }: { layout: HomeLayout; onChange: (l: HomeLayout) => void }) {
  const update = (groups: TileGroup[]) => onChange({ ...layout, groups });
  const addGroup = () => {
    update([
      ...layout.groups,
      {
        id: nid(),
        title: 'New group',
        tiles: [],
      },
    ]);
  };

  return (
    <div className="space-y-3">
      {layout.groups.map((g, i) => (
        <GroupRow
          key={g.id}
          group={g}
          onChange={(next) => {
            const copy = layout.groups.slice();
            copy[i] = next;
            update(copy);
          }}
          onRemove={() =>
            onChange({
              ...layout,
              groups: layout.groups.filter((x) => x.id !== g.id),
              dismissedNav: addDismissed(layout.dismissedNav, g.id),
            })
          }
          onMove={(dir) => {
            const copy = layout.groups.slice();
            const j = i + (dir === 'up' ? -1 : 1);
            if (j < 0 || j >= copy.length) return;
            const a = copy[i]!;
            const b = copy[j]!;
            copy[i] = b;
            copy[j] = a;
            update(copy);
          }}
        />
      ))}
      <button
        onClick={addGroup}
        type="button"
        className="w-full rounded border border-dashed border-stone-300 py-2 text-xs text-stone-500 hover:border-stone-500 hover:text-stone-800"
      >
        + Add group
      </button>
    </div>
  );
}

function GroupRow({
  group,
  onChange,
  onRemove,
  onMove,
}: {
  group: TileGroup;
  onChange: (g: TileGroup) => void;
  onRemove: () => void;
  onMove: (dir: 'up' | 'down') => void;
}) {
  const updateTile = (i: number, tile: Tile) => {
    const copy = group.tiles.slice();
    copy[i] = tile;
    onChange({ ...group, tiles: copy });
  };
  const addTile = () =>
    onChange({
      ...group,
      tiles: [
        ...group.tiles,
        { id: nid(), label: 'New tile', glyph: '◆', color: 'slate', href: '/' },
      ],
    });

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-3 shadow-sm">
      <div className="flex items-center gap-2">
        <input
          value={group.title}
          onChange={(e) => onChange({ ...group, title: e.target.value })}
          className="flex-1 rounded border border-stone-200 px-2 py-1 text-sm font-semibold text-stone-800 focus:border-stone-500 focus:outline-none"
        />
        <button type="button" className="text-stone-400 hover:text-stone-700" onClick={() => onMove('up')}>↑</button>
        <button type="button" className="text-stone-400 hover:text-stone-700" onClick={() => onMove('down')}>↓</button>
        <button type="button" className="text-[#9C4A3A] hover:text-[#7E3A2D]" onClick={onRemove}>×</button>
      </div>
      <div className="mt-2 space-y-2">
        {group.tiles.map((t, j) => (
          <TileEditor
            key={t.id}
            tile={t}
            onChange={(next) => updateTile(j, next)}
            onRemove={() => onChange({ ...group, tiles: group.tiles.filter((x) => x.id !== t.id) })}
            onMove={(dir) => {
              const copy = group.tiles.slice();
              const k = j + (dir === 'up' ? -1 : 1);
              if (k < 0 || k >= copy.length) return;
              const a = copy[j]!;
              const b = copy[k]!;
              copy[j] = b;
              copy[k] = a;
              onChange({ ...group, tiles: copy });
            }}
          />
        ))}
        <button
          type="button"
          onClick={addTile}
          className="w-full rounded border border-dashed border-stone-300 py-1.5 text-[11px] text-stone-500 hover:border-stone-500 hover:text-stone-800"
        >
          + Add tile
        </button>
      </div>
    </div>
  );
}

function TileEditor({
  tile,
  onChange,
  onRemove,
  onMove,
}: {
  tile: Tile;
  onChange: (t: Tile) => void;
  onRemove: () => void;
  onMove: (dir: 'up' | 'down') => void;
}) {
  const swatch = TILE_COLORS[tile.color];
  return (
    <div className="rounded border border-stone-200 bg-stone-50 p-2">
      <div className="flex items-center gap-2">
        <div className={clsx('grid h-8 w-8 place-items-center rounded-md text-sm font-semibold', swatch.bg, swatch.fg)}>
          {tile.glyph ?? tile.label.slice(0, 2).toUpperCase()}
        </div>
        <input
          value={tile.label}
          onChange={(e) => onChange({ ...tile, label: e.target.value })}
          placeholder="Label"
          className="flex-1 rounded border border-stone-200 px-2 py-1 text-xs"
        />
        <button type="button" className="text-stone-400 hover:text-stone-700" onClick={() => onMove('up')}>↑</button>
        <button type="button" className="text-stone-400 hover:text-stone-700" onClick={() => onMove('down')}>↓</button>
        <button type="button" className="text-[#9C4A3A] hover:text-[#7E3A2D]" onClick={onRemove}>×</button>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
        <label className="flex flex-col">
          <span className="text-stone-500">Glyph</span>
          <input
            value={tile.glyph ?? ''}
            onChange={(e) => onChange({ ...tile, glyph: e.target.value })}
            maxLength={4}
            className="rounded border border-stone-200 px-2 py-1"
            placeholder="◆"
          />
        </label>
        <label className="flex flex-col">
          <span className="text-stone-500">Href</span>
          <input
            value={tile.href}
            onChange={(e) => onChange({ ...tile, href: e.target.value })}
            placeholder="/documents"
            className="rounded border border-stone-200 px-2 py-1"
          />
        </label>
      </div>
      <div className="mt-2">
        <div className="text-[11px] text-stone-500">Color</div>
        <div className="mt-1 flex flex-wrap gap-1">
          {TILE_COLOR_OPTIONS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChange({ ...tile, color: c })}
              className={clsx(
                'h-6 w-6 rounded ring-1 ring-stone-300',
                TILE_COLORS[c].bg,
                tile.color === c && 'ring-2 ring-stone-900',
              )}
              title={c}
            />
          ))}
        </div>
      </div>
      <div className="mt-2">
        <div className="text-[11px] text-stone-500">Logo (overrides glyph/color)</div>
        <div className="mt-1">
          <LogoUploader
            value={tile.logoUrl}
            onChange={(url) => onChange({ ...tile, logoUrl: url })}
            prefix="logos/tiles"
            size={40}
            label="Upload"
          />
        </div>
      </div>
      <label className="mt-2 flex items-center gap-1 text-[11px] text-stone-500">
        <input
          type="checkbox"
          checked={!!tile.external}
          onChange={(e) => onChange({ ...tile, external: e.target.checked })}
        />
        External link (open in new tab)
      </label>
    </div>
  );
}

/* ---------------------------------- sidebar ----------------------------------- */

function SidebarEditor({ layout, onChange }: { layout: HomeLayout; onChange: (l: HomeLayout) => void }) {
  const update = (sidebar: SidebarSection[]) => onChange({ ...layout, sidebar });
  return (
    <div className="space-y-3">
      {layout.sidebar.map((section, i) => (
        <div key={section.id} className="rounded-lg border border-stone-200 bg-white p-3 shadow-sm">
          <div className="flex items-center gap-2">
            <input
              value={section.title ?? ''}
              onChange={(e) => {
                const copy = layout.sidebar.slice();
                copy[i] = { ...section, title: e.target.value || undefined };
                update(copy);
              }}
              placeholder="Section title (optional)"
              className="flex-1 rounded border border-stone-200 px-2 py-1 text-sm font-semibold text-stone-800"
            />
            <button
              type="button"
              className="text-[#9C4A3A]"
              onClick={() =>
                onChange({
                  ...layout,
                  sidebar: layout.sidebar.filter((s) => s.id !== section.id),
                  dismissedNav: addDismissed(layout.dismissedNav, section.id),
                })
              }
            >
              ×
            </button>
          </div>
          <div className="mt-2 space-y-1">
            {section.items.map((item, j) => (
              <SidebarItemRow
                key={item.id}
                item={item}
                onChange={(next) => {
                  const items = section.items.slice();
                  items[j] = next;
                  const copy = layout.sidebar.slice();
                  copy[i] = { ...section, items };
                  update(copy);
                }}
                onRemove={() => {
                  const copy = layout.sidebar.slice();
                  copy[i] = { ...section, items: section.items.filter((x) => x.id !== item.id) };
                  onChange({
                    ...layout,
                    sidebar: copy,
                    dismissedNav: addDismissed(layout.dismissedNav, item.id),
                  });
                }}
              />
            ))}
            <button
              type="button"
              onClick={() => {
                const copy = layout.sidebar.slice();
                copy[i] = {
                  ...section,
                  items: [...section.items, { id: nid(), label: 'New', href: '/', icon: '◆' }],
                };
                update(copy);
              }}
              className="w-full rounded border border-dashed border-stone-300 py-1 text-[11px] text-stone-500 hover:border-stone-500"
            >
              + Add item
            </button>
          </div>
        </div>
      ))}
      <button
        onClick={() => update([...layout.sidebar, { id: nid(), items: [] }])}
        type="button"
        className="w-full rounded border border-dashed border-stone-300 py-2 text-xs text-stone-500 hover:border-stone-500"
      >
        + Add section
      </button>
    </div>
  );
}

function SidebarItemRow({
  item,
  onChange,
  onRemove,
}: {
  item: SidebarItem;
  onChange: (i: SidebarItem) => void;
  onRemove: () => void;
}) {
  return (
    // minmax(0,1fr) lets the inputs shrink below their intrinsic min-width so
    // the fixed-width delete button is never pushed off the panel edge.
    <div className="grid grid-cols-[22px_minmax(0,1fr)_minmax(0,1fr)_26px] items-center gap-1 rounded bg-stone-50 px-2 py-1">
      <input
        value={item.icon ?? ''}
        onChange={(e) => onChange({ ...item, icon: e.target.value })}
        className="h-6 w-full rounded border border-stone-200 px-0.5 text-center text-xs"
        maxLength={2}
        placeholder="◆"
      />
      <input
        value={item.label}
        onChange={(e) => onChange({ ...item, label: e.target.value })}
        className="h-6 w-full min-w-0 rounded border border-stone-200 px-2 text-xs"
        placeholder="Label"
      />
      <input
        value={item.href}
        onChange={(e) => onChange({ ...item, href: e.target.value })}
        className="h-6 w-full min-w-0 rounded border border-stone-200 px-2 text-xs"
        placeholder="/path"
      />
      <button
        type="button"
        title="Delete this row"
        aria-label="Delete row"
        onClick={onRemove}
        className="grid h-6 w-6 shrink-0 place-items-center rounded bg-[#9C4A3A] text-sm font-bold text-white hover:bg-[#7E3A2D]"
      >
        ×
      </button>
    </div>
  );
}

/* ---------------------------------- widgets ----------------------------------- */

function WidgetsEditor({ layout, onChange }: { layout: HomeLayout; onChange: (l: HomeLayout) => void }) {
  const update = (rightColumn: Widget[]) => onChange({ ...layout, rightColumn });
  const add = (kind: WidgetKind) => {
    const w = blankWidget(kind);
    update([...layout.rightColumn, w]);
  };
  return (
    <div className="space-y-3">
      {layout.rightColumn.map((w, i) => (
        <WidgetEditor
          key={w.id}
          widget={w}
          onChange={(next) => {
            const copy = layout.rightColumn.slice();
            copy[i] = next;
            update(copy);
          }}
          onRemove={() => update(layout.rightColumn.filter((x) => x.id !== w.id))}
          onMove={(dir) => {
            const copy = layout.rightColumn.slice();
            const j = i + (dir === 'up' ? -1 : 1);
            if (j < 0 || j >= copy.length) return;
            const a = copy[i]!;
            const b = copy[j]!;
            copy[i] = b;
            copy[j] = a;
            update(copy);
          }}
        />
      ))}
      <div className="rounded-lg border border-dashed border-stone-300 p-2 text-center">
        <div className="text-[11px] text-stone-500">Add widget</div>
        <div className="mt-1 flex flex-wrap justify-center gap-1">
          {(['action_required', 'queue', 'note', 'links', 'metric', 'recent_documents', 'processing_status'] as WidgetKind[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => add(k)}
              className="rounded bg-stone-100 px-2 py-1 text-[11px] text-stone-700 hover:bg-stone-200"
            >
              {k.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function WidgetEditor({
  widget,
  onChange,
  onRemove,
  onMove,
}: {
  widget: Widget;
  onChange: (w: Widget) => void;
  onRemove: () => void;
  onMove: (dir: 'up' | 'down') => void;
}) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-3 shadow-sm">
      <div className="flex items-center gap-2">
        <input
          value={widget.title}
          onChange={(e) => onChange({ ...widget, title: e.target.value })}
          className="flex-1 rounded border border-stone-200 px-2 py-1 text-sm font-semibold text-stone-800"
        />
        <span className="text-[10px] uppercase text-stone-400">{widget.kind.replace(/_/g, ' ')}</span>
        <button type="button" className="text-stone-400" onClick={() => onMove('up')}>↑</button>
        <button type="button" className="text-stone-400" onClick={() => onMove('down')}>↓</button>
        <button type="button" className="text-[#9C4A3A]" onClick={onRemove}>×</button>
      </div>
      <div className="mt-2 text-[11px]">
        {widget.kind === 'note' && (
          <textarea
            value={widget.body}
            onChange={(e) => onChange({ ...widget, body: e.target.value })}
            rows={4}
            className="w-full rounded border border-stone-200 px-2 py-1 text-xs"
          />
        )}
        {widget.kind === 'queue' && (
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col">
              <span className="text-stone-500">Source</span>
              <select
                value={widget.source}
                onChange={(e) => onChange({ ...widget, source: e.target.value as any })}
                className="rounded border border-stone-200 px-2 py-1 text-xs"
              >
                <option value="triage">Triage</option>
                <option value="bills_due">Bills due</option>
                <option value="bills_past_due">Bills past due</option>
                <option value="static">Static list</option>
              </select>
            </label>
            <label className="flex flex-col">
              <span className="text-stone-500">Limit</span>
              <input
                type="number"
                min={1}
                max={20}
                value={widget.limit}
                onChange={(e) => onChange({ ...widget, limit: Number(e.target.value) || 3 })}
                className="rounded border border-stone-200 px-2 py-1 text-xs"
              />
            </label>
          </div>
        )}
        {widget.kind === 'recent_documents' && (
          <label className="flex flex-col">
            <span className="text-stone-500">Limit</span>
            <input
              type="number"
              min={1}
              max={20}
              value={widget.limit}
              onChange={(e) => onChange({ ...widget, limit: Number(e.target.value) || 5 })}
              className="rounded border border-stone-200 px-2 py-1 text-xs"
            />
          </label>
        )}
        {widget.kind === 'action_required' && (
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col">
              <span className="text-stone-500">Primary label</span>
              <input
                value={widget.primaryLabel ?? ''}
                onChange={(e) => onChange({ ...widget, primaryLabel: e.target.value })}
                className="rounded border border-stone-200 px-2 py-1 text-xs"
              />
            </label>
            <label className="flex flex-col">
              <span className="text-stone-500">Secondary label</span>
              <input
                value={widget.secondaryLabel ?? ''}
                onChange={(e) => onChange({ ...widget, secondaryLabel: e.target.value })}
                className="rounded border border-stone-200 px-2 py-1 text-xs"
              />
            </label>
            <label className="col-span-2 flex flex-col">
              <span className="text-stone-500">Inbox href</span>
              <input
                value={widget.inboxHref}
                onChange={(e) => onChange({ ...widget, inboxHref: e.target.value })}
                className="rounded border border-stone-200 px-2 py-1 text-xs"
              />
            </label>
          </div>
        )}
        {widget.kind === 'metric' && (
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col">
              <span className="text-stone-500">Source</span>
              <select
                value={widget.source}
                onChange={(e) => onChange({ ...widget, source: e.target.value as any })}
                className="rounded border border-stone-200 px-2 py-1 text-xs"
              >
                <option value="triage">Triage</option>
                <option value="bills_due_week">Bills due this week</option>
                <option value="bills_past_due">Bills past due</option>
                <option value="documents_total">Total documents</option>
              </select>
            </label>
            <label className="flex flex-col">
              <span className="text-stone-500">Caption</span>
              <input
                value={widget.caption ?? ''}
                onChange={(e) => onChange({ ...widget, caption: e.target.value })}
                className="rounded border border-stone-200 px-2 py-1 text-xs"
              />
            </label>
          </div>
        )}
        {widget.kind === 'links' && (
          <div className="space-y-1">
            {widget.links.map((l, i) => (
              <div key={l.id} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_26px] items-center gap-1">
                <input
                  value={l.label}
                  onChange={(e) => {
                    const links = widget.links.slice();
                    links[i] = { ...l, label: e.target.value };
                    onChange({ ...widget, links });
                  }}
                  placeholder="Label"
                  className="w-full min-w-0 rounded border border-stone-200 px-2 py-1 text-xs"
                />
                <input
                  value={l.href}
                  onChange={(e) => {
                    const links = widget.links.slice();
                    links[i] = { ...l, href: e.target.value };
                    onChange({ ...widget, links });
                  }}
                  placeholder="https://…"
                  className="w-full min-w-0 rounded border border-stone-200 px-2 py-1 text-xs"
                />
                <button
                  type="button"
                  title="Delete link"
                  aria-label="Delete link"
                  className="grid h-6 w-6 shrink-0 place-items-center rounded bg-[#9C4A3A] text-sm font-bold text-white hover:bg-[#7E3A2D]"
                  onClick={() => onChange({ ...widget, links: widget.links.filter((x) => x.id !== l.id) })}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => onChange({ ...widget, links: [...widget.links, { id: nid(), label: '', href: '', external: true }] })}
              className="w-full rounded border border-dashed border-stone-300 py-1 text-[11px] text-stone-500 hover:border-stone-500"
            >
              + Add link
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function blankWidget(kind: WidgetKind): Widget {
  switch (kind) {
    case 'action_required':
      return { id: nid(), kind, title: 'Action required', inboxHref: '/documents/triage' };
    case 'queue':
      return { id: nid(), kind, title: 'My queue', source: 'triage', limit: 3 };
    case 'note':
      return { id: nid(), kind, title: 'Note', body: 'Write something…' };
    case 'links':
      return { id: nid(), kind, title: 'Links', links: [] };
    case 'metric':
      return { id: nid(), kind, title: 'Metric', source: 'documents_total' };
    case 'recent_documents':
      return { id: nid(), kind, title: 'Recently added', limit: 5 };
    case 'processing_status':
      return { id: nid(), kind, title: 'Document processing' };
  }
}

/* ----------------------------------- brand ------------------------------------ */

function BrandEditor({ layout, onChange }: { layout: HomeLayout; onChange: (l: HomeLayout) => void }) {
  return (
    <div className="space-y-3 rounded-lg border border-stone-200 bg-white p-3 shadow-sm">
      <label className="flex flex-col text-xs">
        <span className="text-stone-500">Display name</span>
        <input
          value={layout.brand.name}
          onChange={(e) => onChange({ ...layout, brand: { ...layout.brand, name: e.target.value } })}
          className="rounded border border-stone-200 px-2 py-1 text-sm"
        />
      </label>
      <label className="flex flex-col text-xs">
        <span className="text-stone-500">Tagline</span>
        <input
          value={layout.brand.tagline ?? ''}
          onChange={(e) => onChange({ ...layout, brand: { ...layout.brand, tagline: e.target.value } })}
          className="rounded border border-stone-200 px-2 py-1 text-sm"
        />
      </label>
      <div>
        <div className="text-[11px] text-stone-500">Logo (used in the sidebar and as the browser tab icon)</div>
        <div className="mt-1">
          <LogoUploader
            value={layout.brand.logoUrl}
            onChange={(url) => onChange({ ...layout, brand: { ...layout.brand, logoUrl: url } })}
            prefix="logos/brand"
            label="Upload SFP logo"
          />
        </div>
        <div className="mt-1 text-[10px] italic text-stone-400">
          Falls back to the glyph below if no logo is uploaded.
        </div>
      </div>
      <label className="flex flex-col text-xs">
        <span className="text-stone-500">Mark glyph (fallback)</span>
        <input
          value={layout.brand.markGlyph ?? ''}
          maxLength={2}
          onChange={(e) => onChange({ ...layout, brand: { ...layout.brand, markGlyph: e.target.value } })}
          className="w-16 rounded border border-stone-200 px-2 py-1 text-center text-lg"
        />
      </label>
    </div>
  );
}

/* helpers */
let _seq = 0;
function nid(): string {
  _seq += 1;
  return `id_${_seq.toString(36)}_${Math.floor(performance.now()).toString(36)}`;
}

/**
 * Record that a built-in (default) section/item/group id was deleted, so the
 * self-healing migration won't re-add it on the next load. Harmless for custom
 * ids (they never match a default).
 */
function addDismissed(current: string[] | undefined, id: string): string[] {
  const set = new Set(current ?? []);
  set.add(id);
  return [...set];
}

export type { TileColor };
